import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CircleFlag } from 'react-circle-flags';
import Lottie from 'lottie-react';
import entityNameAnimation from '../../../images/building-lottie.json';
import iconIncorporationCountry from '../../../images/earth-location.svg';
// Same icons as Customer identification and the ownership / signatory cards.
import iconNationality from '../../../images/passport.svg';
import iconDomicile from '../../../images/address.svg';
import iconBirthday from '../../../images/birthday.svg';
import iconSigning from '../../../images/affiliate-marketing.svg';
import iconShares from '../../../images/analytics.svg';
import iconPerson from '../../../images/person-circle.svg';
import iconCompany from '../../../images/businessTrade.svg';
import Modal from '../../../components/ui/Modal';
import { BottomSheet } from './KycBottomSheet';
import { COMPANY_READING_FIELDS, companyReadingSummary } from '../../../utils/companyDocumentReading';
import { useLockBodyScroll } from '../common/common_utils/useLockBodyScroll';

const ROLE_LABELS = {
  director: 'Director', manager: 'Manager', president: 'President', secretary: 'Secretary', treasurer: 'Treasurer',
  officer: 'Officer', legal_representative: 'Legal representative', signatory: 'Signatory',
};
const SIGNING_POWER_LABELS = { sole: 'Sole signature', joint: 'Joint signature' };
// The document by what it is (never its file name): the matched list entry, otherwise "Unrecognized document".
const documentLabel = d => d.matchedDocument || 'Unrecognized document';

const countryNames = (() => {
  try { return new Intl.DisplayNames(['en'], { type: 'region' }); } catch { return null; }
})();
const countryName = iso2 => {
  try { return countryNames?.of(iso2) || iso2; } catch { return iso2; }
};
// "2016-03-02" -> "2 March 2016"
const readableDate = value => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return value || '';
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date) : value;
};

function Country({ code }) {
  if (!/^[A-Z]{2}$/.test(code || '')) return null;
  return <span className="kycReadingCountry">
    <CircleFlag countryCode={code.toLowerCase()} height="16" aria-hidden="true" />{countryName(code)}
  </span>;
}

// "please check" tags on uncertain values: hidden for now (kept to switch back on).
const SHOW_CHECK_TAGS = false;
const CheckTag = ({ options }) => SHOW_CHECK_TAGS && options.some(o => o.uncertain) ? <em className="kycReadingCheck">please check</em> : null;

// Other documents' values for a detail, under the first document's value.
const OtherValues = ({ options, render = value => value }) => options.length > 1
  ? <small className="kycReadingDiffers">Another document shows: {options.slice(1).map((o, i) => <span key={i}>{i ? ', ' : ''}{render(o.value)}</span>)}</small>
  : null;

// A shareholder (individual or company) or a signatory: kept (ticked) or removed. Three columns on desktop (fixed
// widths, so several people line up): name and badges | signing power or shares (and date of birth) | nationality
// and domicile (a company: country of incorporation).
function PersonCard({ person, kind, onToggle }) {
  const company = person.company === true;
  const roles = kind === 'officer' ? (person.roles || []).map(role => ROLE_LABELS[role] || role) : company ? ['Company'] : [];
  // Middle column: icon and value only.
  const middle = [
    kind === 'officer' && { key: 'signing', icon: iconSigning, value: SIGNING_POWER_LABELS[person.signingPower] || 'Signing power not stated' },
    kind === 'shareholder' && Number.isFinite(person.sharePercent) && { key: 'share', icon: iconShares, value: `${person.sharePercent}% of shares` },
    person.dateOfBirth && { key: 'dob', icon: iconBirthday, value: `Born ${readableDate(person.dateOfBirth)}` },
  ].filter(Boolean);
  // Right column: labelled.
  const side = company ? [
    person.country && { key: 'country', icon: iconIncorporationCountry, label: 'Incorporated in', code: person.country },
  ].filter(Boolean) : [
    person.nationality && { key: 'nationality', icon: iconNationality, label: 'Nationality', code: person.nationality },
    person.countryOfResidence && { key: 'domicile', icon: iconDomicile, label: 'Domicile', code: person.countryOfResidence },
  ].filter(Boolean);
  return <li className={`kycReadingPerson${person.included ? '' : ' is-excluded'}`}>
    <div className="kycReadingPersonMain">
      <label className="kycReadingPersonHead">
        <input type="checkbox" checked={person.included} onChange={event => onToggle(event.target.checked)}
          aria-label={`Keep ${person.name || 'this person'}`} />
        <img className="kycReadingPersonIcon" src={company ? iconCompany : iconPerson} alt="" aria-hidden="true" />
        <span className="kycReadingPersonName">
          <strong>{person.name || 'Unnamed'}</strong>
          {roles.length > 0 && <span className="kycReadingRoles">{roles.map(role => <span key={role} className="kycReadingRole">{role}</span>)}</span>}
        </span>
      </label>
      {person.uncertain && <p className="kycReadingPersonCheck">Partly unclear on the document, please check</p>}
    </div>
    <ul className="kycReadingPersonMiddle">{middle.map(fact => <li key={fact.key}>
      <img src={fact.icon} alt="" aria-hidden="true" />{fact.value}
    </li>)}</ul>
    <dl className="kycReadingPersonSide">{side.map(fact => <div key={fact.key} className="kycReadingFact">
      <img src={fact.icon} alt="" aria-hidden="true" />
      <div><dt>{fact.label}</dt><dd><Country code={fact.code} /></dd></div>
    </div>)}</dl>
  </li>;
}

function PeopleSection({ title, people, setPeople, kind }) {
  if (!people.length) return null;
  const toggle = (index, included) => setPeople(list => list.map((person, i) => i === index ? { ...person, included } : person));
  return <section className="kycReadingSection">
    <h3>{title}</h3>
    <ul className="kycReadingPeople">{people.map((person, index) =>
      <PersonCard key={index} person={person} kind={kind} onToggle={included => toggle(index, included)} />)}</ul>
  </section>;
}

/**
 * "Details found in your documents": what the reader found in the uploaded company documents, read only.
 * The client keeps or removes each signatory and shareholder; onConfirm(reading) receives the kept details
 * (company values from the first document showing them); onSkip continues without them. Uploaded files are
 * kept in both cases.
 */
export default function KycCompanyReadingReview({ documents, mobile = false, onConfirm, onSkip, onClose, busy = false }) {
  const summary = useMemo(() => companyReadingSummary(documents), [documents]);
  const company = useMemo(() => Object.fromEntries(COMPANY_READING_FIELDS.map(({ key }) => [key, summary.company[key][0]?.value || ''])), [summary]);
  const [officers, setOfficers] = useState(() => summary.officers.map(person => ({ ...person, included: true })));
  // Companies first, then individuals (one list, so the client keeps or removes each of them).
  const [shareholders, setShareholders] = useState(() => [
    ...summary.companyShareholders.map(company => ({ ...company, company: true, included: true })),
    ...summary.shareholders.map(person => ({ ...person, included: true })),
  ]);
  useLockBodyScroll(!mobile);

  const confirm = () => onConfirm({
    confirmedAt: new Date().toISOString(),
    company: { ...company, legalForm: summary.legalForm },
    officers: officers.filter(p => p.included).map(({ included, uncertain, ...person }) => person),
    shareholders: shareholders.filter(p => p.included && !p.company).map(({ included, uncertain, roles, ...person }) => person),
    corporateShareholders: summary.corporateShareholders,
    companyShareholders: shareholders.filter(p => p.included && p.company).map(({ included, uncertain, company, ...rest }) => rest),
    documents: summary.documents,
  });

  const label = key => COMPANY_READING_FIELDS.find(field => field.key === key)?.label || key;
  // Documents worth a line: an issue date or a country note.
  const issued = summary.documents.filter(d => d.issueDate || d.countryNote);
  const body = <div className="kycReadingReview">
    {/* Company as an identity card: name as the headline, key facts as pills, the rest as quiet rows. */}
    <section className="kycReadingSection kycReadingIdentity" aria-label="Company">
      <div className="kycReadingIdentityHead">
        {/* Same animated building as the entity name in Customer identification. */}
        <span className="kycReadingIdentityIcon" aria-hidden="true"><Lottie animationData={entityNameAnimation} loop autoplay /></span>
        <div className="kycReadingIdentityMain">
      {summary.company.name.length > 0 && <p className="kycReadingIdentityName">
        {summary.company.name[0].value}<CheckTag options={summary.company.name} />
        <OtherValues options={summary.company.name} />
      </p>}
      {(summary.company.country.length > 0 || summary.legalForm || summary.company.incorporationDate.length > 0) &&
        <div className="kycReadingPills">
          {summary.company.country.length > 0 && <span className="kycReadingPill" title={label('country')}><Country code={summary.company.country[0].value} /></span>}
          {summary.legalForm && <span className="kycReadingPill" title="Legal form">{summary.legalForm}</span>}
          {summary.company.incorporationDate.length > 0 && <span className="kycReadingPill" title={label('incorporationDate')}>
            Since {readableDate(summary.company.incorporationDate[0].value)}</span>}
        </div>}
      {(summary.company.country.length > 1 || summary.company.incorporationDate.length > 1) && <p className="kycReadingPillsOther">
        <OtherValues options={summary.company.country} render={code => countryName(code)} />
        <OtherValues options={summary.company.incorporationDate} render={readableDate} />
      </p>}
        </div>
      </div>
      {/* Address with the city at the end: "S01 – … – Al Quoz Industrial Area 4 · Dubai". */}
      {(summary.company.address.length > 0 || summary.company.city.length > 0) && <div className="kycReadingAddress">
        {/* Same icon as "Incorporation country" in Customer identification, without its tile. */}
        <img className="kycReadingAddressIcon" src={iconIncorporationCountry} alt="" aria-hidden="true" />
        <dl>
          <dt>Address<CheckTag options={[...summary.company.address, ...summary.company.city]} /></dt>
          <dd>
            {[summary.company.address[0]?.value, summary.company.city[0]?.value].filter(Boolean).join(' · ')}
            <OtherValues options={summary.company.address} />
            <OtherValues options={summary.company.city} />
          </dd>
        </dl>
      </div>}
    </section>

    <PeopleSection title="Directors and signatories" people={officers} setPeople={setOfficers} kind="officer" />
    <PeopleSection title="Shareholders" people={shareholders} setPeople={setShareholders} kind="shareholder" />
    {/* A company among the shareholders: multi-layer ownership, completed in Ownership & control (UBOs included). */}
    {summary.corporateShareholders && <p className="kycReadingWarning" role="note">
      <i className="bi bi-info-circle" aria-hidden="true" /> One of the shareholders seems to be a company{summary.companyShareholders.length > 0 && ` (${summary.companyShareholders.map(company => company.name).join(', ')})`}, so the ownership may have several layers. You'll complete Ownership &amp; control yourself, including the individuals who ultimately control that company.
    </p>}
    {/* Source document(s): a small footnote at the bottom of the modal. */}
    {issued.length > 0 && <div className="kycReadingDocuments">
      {issued.map(d => <p key={d.name} className="kycReadingDocument">
        
        {documentLabel(d)}
        {d.countryNote && <span className="kycReadingDocumentNote"> · <i className="bi bi-exclamation-triangle" aria-hidden="true" /> {d.countryNote}</span>}
        {d.issueDate && ` · Issued ${readableDate(d.issueDate)}`}
      </p>)}
    </div>}
  </div>;

  const actions = <div className="kycReadingActions">
    <button type="button" className="kycReadingSkip" disabled={busy} onClick={onSkip}>Fill in myself</button>
    <button type="button" className="kycReadingConfirm" disabled={busy} onClick={confirm}>Use these details</button>
  </div>;

  if (mobile) {
    return <BottomSheet open title="Details found in your documents" onClose={onClose} className="kycReadingSheet">
      {body}
      <div className="kycReadingSheetActions">{actions}</div>
    </BottomSheet>;
  }
  return createPortal(<Modal open title="Details found in your documents" className="kycReadingModal" onClose={onClose} footer={actions}>
    {body}
  </Modal>, document.body);
}
